import type { MonitorRoomSummary } from '@tctp/protocol';
import { type FormEvent, useEffect, useState } from 'react';
import { storeMonitorKey, storedMonitorKey } from '../identity';
import { navigate } from '../router';

export const TOO_MANY = 'Too many attempts. Try again in 15 minutes.';
const POLL_MS = 3_000;

function status(r: MonitorRoomSummary): { text: string; cls: string } {
  if (r.ended) return { text: 'ENDED', cls: 'ended' };
  if (r.paused) return { text: 'PAUSED', cls: 'paused' };
  if (r.phase !== null) return { text: `HAND ${r.handNumber} · ${r.phase.toUpperCase()}`, cls: 'live' };
  if (r.running) return { text: r.handNumber === 0 ? 'WAITING' : `HAND ${r.handNumber} · BETWEEN HANDS`, cls: 'live' };
  return { text: 'LOBBY', cls: 'lobby' };
}

const ago = (t: number, now: number): string => `${Math.max(0, Math.round((now - t) / 60_000))} min ago`;

/** The operator's list of live games at `/<path>`; the key is typed once and kept in localStorage until the server rejects it. */
export function MonitorPage({ path }: { path: string }) {
  const [key, setKey] = useState<string | null>(storedMonitorKey);
  const [input, setInput] = useState('');
  const [rooms, setRooms] = useState<MonitorRoomSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (key === null) return;
    let stopped = false;
    async function poll(): Promise<void> {
      try {
        const res = await fetch(`/${path}/rooms`, { headers: { 'x-monitor-key': key! } });
        if (stopped) return;
        if (res.status === 401) { storeMonitorKey(null); setKey(null); setRooms(null); setError('Wrong key'); return; }
        if (res.status === 429) { setError(TOO_MANY); return; }
        if (!res.ok) throw new Error(String(res.status));
        setRooms(((await res.json()) as { rooms: MonitorRoomSummary[] }).rooms);
        setNow(Date.now());
        setError(null);
      } catch {
        if (!stopped) setError('Server unreachable');
      }
    }
    const tick = (): void => { if (document.visibilityState !== 'hidden') void poll(); };
    tick();
    const timer = window.setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [key, path]);

  function unlock(e: FormEvent): void {
    e.preventDefault();
    if (/[^\x20-\x7e]/.test(input)) { setError('Wrong key'); return; }   // the server only accepts printable ASCII, and fetch cannot send more as a header
    storeMonitorKey(input);
    setKey(input);
    setInput('');
    setError(null);
  }

  return (
    <main className="monitor">
      <h1>MONITOR</h1>
      {error && <p className="error" role="alert">{error}</p>}
      {key === null ? (
        <form onSubmit={unlock}>
          <label>Monitor key<input type="password" value={input} onChange={e => setInput(e.target.value)} autoFocus /></label>
          <button className="btn green" type="submit" disabled={input.length === 0}>UNLOCK</button>
        </form>
      ) : rooms === null ? (
        !error && <p className="muted">Loading…</p>
      ) : rooms.length === 0 ? (
        <p className="muted">No games right now.</p>
      ) : (
        <ul className="rooms">
          {rooms.map(r => {
            const st = status(r);
            return (
              <li key={r.roomId} className="mon-room">
                <div className="mon-head"><span className="mon-id">{r.roomId}</span><span className={`mon-pill ${st.cls}`}>{st.text}</span></div>
                <button type="button" className="btn green small" onClick={() => navigate(`/${path}/r/${r.roomId}`)}>OPEN</button>
                <div className="mon-meta">Host: {r.hostNickname ?? '—'} · Pot {r.potTotal} · {ago(r.lastActivityAt, now)}</div>
                <div className="mon-players">
                  {r.players.map(p => <span key={p.seat}><span className={`dot${p.connected ? ' on' : ''}`} aria-hidden="true" />{p.nickname} <b>{p.stack}</b></span>)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
